import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Page,
  Icon,
  Spinner,
  ButtonSelect,
  FormGroup,
  FormControl,
  Input,
  Dropdown,
  Button,
  Alert
} from "components";
import classNames from "lib/classNames";
import ShopContext from "context/ShopContext";
import UserContext from "context/UserContext";
import { Scrollbars } from "react-custom-scrollbars";
import { API_GATEWAY_NEW } from "aws-exports";
import styles from "styles/page.module.scss";
import { API } from "aws-amplify";
import { useTransition } from "react-spring";
import removeTypeName from "lib/removeTypeName";
import useRefState from "hooks/useRefState";
import useReactRouter from 'use-react-router';

const defaultCartLinks = [{ selector: 'a[href="/cart"]', action: "TOGGLE" }];
const actions = [
  { value: "TOGGLE", label: "Toggle Cart" },
  { value: "OPEN", label: "Open Cart" },
  { value: "CLOSE", label: "Close Cart" }
];

const ManualSetup = () => {
  const [activePreviewDevice, setActivePreviewDevice] = useState("desktop");
  const is = value => activePreviewDevice === value
  const [currentAction, setCurrentAction] = useState();
  const [unsaved, setUnsaved] = useState(false);
  const shopContext = useContext(ShopContext);
  const user = useContext(UserContext);
  const { state } = shopContext;
  const [iframeSrc, setIframeSrc] = useState(
    `${API_GATEWAY_NEW}/storeFront?shop=${user.attributes.website}&manual_setup=true&origin=${window.location.origin}`
  );
  const { history, location } = useReactRouter();
  const iframe = useRef(null);
  const selectorOverlay = useRef(null);
  const [shopCustomLinks, setShopCustomLinks] = useState(
    removeTypeName(state.custom_links || [])
  );
  const [shopCustomATCs, setShopCustomATCs] = useState(
    removeTypeName(state.custom_atcs || [])
  );
  const [customCartLinks, setCustomCartLinks, cclRef] =
    useRefState(shopCustomLinks);
  const [customATCs, setCustomATCs, catcRef] =
    useRefState(shopCustomATCs);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const discard = () => {
    setCustomCartLinks(shopCustomLinks);
    sendMessage({
      action: "RESET_LINKS",
      selectors: shopCustomLinks.map(({ selector }) => selector)
    });
  };

  const save = async () => {
    setSaveLoading(true);
    await API.post("API_GATEWAY_NEW", "/cart/links", {
      body: {
        custom_links: cclRef.current,
        custom_atcs: catcRef.current,
      }
    });
    setShopCustomLinks(cclRef.current);
    setShopCustomATCs(catcRef.current);
    setSaveLoading(false);
  };

  const sendMessage = (data) => {
    if (iframe.current)
      iframe.current.contentWindow.postMessage(data, API_GATEWAY_NEW);
  };

  const handleMessage = (event) => {
    const { action, value } = event.data;
    console.log(event.data);
    switch (action) {
      case "SELECT_CART_LINK":
        if (!cclRef.current.find(v => v.selector === value)) {
          setCustomCartLinks(
            [...cclRef.current].concat([{
              selector: value,
              action: "TOGGLE"
            }])
          )
        }
        else {
          setCustomCartLinks(
            [...cclRef.current].filter(v => v.selector !== value)
          )
        }
        break;
      case "SELECT_ATC":
        console.log(value);
        if (!catcRef.current.find(v => v.button === value.button)) {
          setCustomATCs(
            [...catcRef.current].concat([value])
          )
        }
        else {
          setCustomATCs(
            [...catcRef.current].filter(v => v.button !== value.button)
          )
        }
        break;
    }
  };

  const [elementPickerEnabled, setElementPickerEnabled] = useState(false);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);
  useEffect(() => {
    if (currentAction) {
      sendMessage({
        action: "VALUE_CHANGED",
        value: {
          currentAction,
          customCartLinks
        }
      })
    }
  }, [currentAction, customCartLinks])

  useEffect(() => {
    console.log("unsaved", JSON.stringify({ l: shopCustomLinks, b: shopCustomATCs }) !== JSON.stringify({ l: customCartLinks, b: customATCs }));
    setUnsaved(JSON.stringify({ l: shopCustomLinks, b: shopCustomATCs }) !== JSON.stringify({ l: customCartLinks, b: customATCs }))
  }, [shopCustomATCs, shopCustomLinks, customATCs, customCartLinks])

  return (
    <Page
      background="white"
      padding="p-0"
      header={{ title: "Manual Setup" }}
    >
      <div className={classNames("fixed z-20 bg-white inset-0 flex flex-col")}>
        <nav className={classNames("flex py-2 px-4 items-center justify-between bg-gray-800 text-white font-bold text-lg")}>

          <div className={classNames("flex-1")}>
            <div className={classNames("flex w-48 border border-gray-700 rounded overflow-hidden")}>
              <div className={classNames("py-1 flex-1 text-sm text-center rounded-l", {
                "bg-gray-700 pointer-events-none": is("desktop"),
                "cursor-pointer hover:bg-gray-900": is("mobile"),
              })} disabled={is("desktop")} onClick={() => setActivePreviewDevice("desktop")}>Desktop</div>
              <div className={classNames(" py-1 flex-1 text-sm text-center rounded-r", {
                "bg-gray-700 pointer-events-none": is("mobile"),
                "cursor-pointer hover:bg-gray-900": is("desktop")
              })} disabled={is("mobile")} onClick={() => setActivePreviewDevice("mobile")}>Mobile</div>
            </div>
          </div>
          <div className={classNames("flex-1 flex-center")}>
            <button className={classNames("py-1 px-2 text-sm text-center rounded cursor-pointer border border-gray-700 hover:bg-gray-900", {
              hidden: !currentAction
            })} onClick={() => {
              const newState = currentAction === "done" ? undefined : "done"
              setCurrentAction(newState)
            }}>{
                currentAction === "done" ? "Select an action " : !currentAction ? "Change Action" : "Done editing"}</button>
          </div>
          <div className={classNames("w-48 flex items-center flex-1 justify-end")}>
            <p className={classNames("text-sm text-white mr-3", {
              hidden: !unsaved
            })}>Unsaved changes</p>
            <button className={classNames("py-1 px-4 text-sm bg-red-500 hover:bg-red-600 rounded")} onClick={() => history.push("/settings")}>Close</button>
            <button
              disabled={!unsaved}
              className={classNames("py-1 px-4 text-sm disabled:bg-gray-500 bg-blue-500 hover:bg-blue-600 rounded ml-2")} onClick={() => {
                setCurrentAction("done")
                save()
              }}>Save</button>
          </div>
        </nav>
        <div className={classNames("flex-1 h-full relative")} >
          <div className={classNames("absolute inset-0 z-20 bg-black bg-opacity-75 backdrop-blur-sm flex-center", {
            "hidden": currentAction || loading
          })}>
            <div className={classNames("bg-white rounded px-5 py-10 min-w-md")}>
              <FormControl label="Manually setup cart trigger buttons" info="Not every theme is created equal.<br/>You may need to manually select your cart drawer triggers.<br/>If you have any trouble with this please reach out to the team.">
                <ButtonSelect
                  options={[
                    { value: "atc_button", icon: "cart-plus", info: "Specify your products add to cart buttons", label: "Add to cart button" },
                    {
                      value: "cart_link", icon: "shopping-cart", info: "Specify elements that will open the cart drawer", label: "Open cart links"
                    },
                  ]}
                  value={currentAction}
                  onChange={(value) => setCurrentAction(value)}
                  className={classNames("mt-4")}
                />
              </FormControl>
            </div>
          </div>
          <div className={classNames("w-full h-full relative")}>
            {loading && <Spinner text="loading storefront" />}
            <iframe
              ref={iframe}
              onLoad={() => {
                sendMessage({
                  action: "VALUE_CHANGED",
                  value: {
                    currentAction,
                    customCartLinks
                  }
                })
                setLoading(false);
              }}
              src={iframeSrc}
              className={classNames("h-full mx-auto", loading && "hidden")}
              style={{
                width: activePreviewDevice === "desktop" ? "100%" : 350
              }}
              onMouseLeave={() => {
                if (elementPickerEnabled && selectorOverlay.current)
                  selectorOverlay.current.style.display = "flex";
              }}
            />
            {elementPickerEnabled && (
              <div
                ref={selectorOverlay}
                className={classNames(
                  "absolute inset-0 z-20 top-0 w-full h-full text-white text-2xl flex-center"
                )}
                style={{ background: "rgba(0,0,0,0.6)" }}
                onMouseEnter={({ target }) => (target.style.display = "none")}
              >
                Click on the element to select it.
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default ManualSetup;

//'ritzystore2.myshopify.com' ||

const CartLink = ({ link, updateLink, removeLink }) => {
  const isDefault = defaultCartLinks.includes(link);
  return (
    <div className={classNames("p-3 rounded bg-white mb-2 shadow-sm")}>
      <FormControl
        label={`${isDefault ? "default" : "additional"} trigger`}
        marginBottom="mb-3"
      >
        <Input
          type="text"
          value={link.selector}
          onChange={(v) => console.log(v)}
          disabled={isDefault}
        />
      </FormControl>
      {/* <FormControl label="action" marginBottom="mb-3">
				<Dropdown options={actions} value={link.action} disabled={true} onChange={(v) => console.log(v)} />
			</FormControl> */}
      {!isDefault && (
        <div className={classNames("flex flex-row-reverse")}>
          <button
            className={classNames(
              "cursor-pointer focus:outline-none text-center text-red-500 text-sm"
            )}
            onClick={() => removeLink(link.selector)}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
};
